// Analytics tracking service for TreeGenie
import { collection, addDoc, getDocs, query, where, orderBy, limit, Timestamp, doc, getDoc, getCountFromServer } from 'firebase/firestore';
import * as firestorePaths from './firestorePaths'; // Import the new path utility
import { updateSessionActivity } from './sessionManager';

class AnalyticsService {
  constructor(db, userId, userEmail, userTeam, userTeamName, isAdmin = false) {
    this.db = db;
    this.userId = userId;
    this.userEmail = userEmail;
    this.userTeam = userTeam;
    this.userTeamName = userTeamName || null; // Default to null to prevent undefined in Firestore
    this.isAdmin = isAdmin;
        
    console.log('Analytics: Service initialized with:', {
      userId,
      isAdmin,
      userEmail: userEmail ? '[REDACTED]' : 'null',
      userTeam,
      userTeamName
    });
        
    this.currentSessionId = null;
    this.sessionStartTime = null;
    this.lastActivityTime = null;
    // Removed inactivity tracking variables
    this.isTracking = false;
    this.lastActivityEventTime = null;
    this.activityEventThrottle = 30 * 1000; // Only track activity events every 30 seconds
  }

  // Initialize analytics tracking
  init() {
    if (!this.db || !this.userId) {
      console.warn('Analytics: Cannot initialize without db and userId');
      return;
    }

    this.isTracking = true;
    this.startNewSession();
    this.setupActivityTracking();
    console.log('Analytics service initialized for user:', this.userEmail);
  }

  // Start a new session
  startNewSession() {
    this.currentSessionId = this.generateSessionId();
    this.sessionStartTime = new Date();
    this.lastActivityTime = new Date();
        
    this.trackEvent('session_start', {
      sessionId: this.currentSessionId,
      userAgent: navigator.userAgent,
      timestamp: Timestamp.fromDate(this.sessionStartTime)
    });
  }
    
  // Set session ID to match call session
  setSessionId(sessionId) {
    console.log('Analytics: Setting session ID to:', sessionId);
    this.currentSessionId = sessionId;
    if (!this.sessionStartTime) {
      this.sessionStartTime = new Date();
      this.lastActivityTime = new Date();
            
      this.trackEvent('session_start', {
        sessionId: this.currentSessionId,
        userAgent: navigator.userAgent,
        timestamp: Timestamp.fromDate(this.sessionStartTime)
      });
    }
  }

  // Track call events with timeout protection
  async trackCallEvent(eventType, eventData = {}) {
    if (!this.isTracking) return;

    try {
      const appId = firestorePaths.getArtifactProjectId();
            
      let analyticsRef;
            
      if (!this.userTeam) {
        console.warn('Analytics: No team context — skipping call event tracking');
        return;
      }
      analyticsRef = collection(this.db, firestorePaths.getCallEventsPath(appId, this.userTeam));
            
      const eventDoc = {
        userId: this.userId,
        userEmail: this.userEmail,
        userTeam: this.userTeam,
        userTeamName: this.userTeamName,
        sessionId: this.currentSessionId,
        eventType, // 'call_start', 'call_complete', 'call_reset', 'node_visited', 'lead_generated'
        timestamp: Timestamp.now(),
        ...eventData
      };

      // Add timeout protection for analytics (don't let analytics block other operations)
      const analyticsPromise = addDoc(analyticsRef, eventDoc);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Analytics timeout after 5 seconds')), 5000)
      );

      await Promise.race([analyticsPromise, timeoutPromise]);
      console.log(`Analytics: Tracked ${eventType}`, eventData);
    } catch (error) {
      console.warn('Analytics: Error tracking call event (non-critical):', error);
      // Don't re-throw - analytics failures shouldn't break the main flow
    }
  }

  // Track user activity
  async trackActivity() {
    if (!this.isTracking) {
      // This should rarely happen now since listeners aren't attached when disabled
      console.warn('Analytics: trackActivity called but tracking disabled - this indicates a setup issue');
      return;
    }
        
    if (!this.currentSessionId) {
      console.log('Analytics: trackActivity called but no session ID - starting new session');
      this.startNewSession();
    }

    this.lastActivityTime = new Date();
        
    // Track activity event to Firestore (throttled to avoid too many events)
    const now = this.lastActivityTime.getTime();
    if (!this.lastActivityEventTime || (now - this.lastActivityEventTime) >= this.activityEventThrottle) {
      console.log('Analytics: Tracking user_activity event for session:', this.currentSessionId);
      this.trackEvent('user_activity', {
        sessionId: this.currentSessionId,
        timestamp: Timestamp.fromDate(this.lastActivityTime)
      });
      this.lastActivityEventTime = now;
    }

    // Update presence heartbeat on the active session doc (throttled internally to 5 min)
    if (this.userId) {
      updateSessionActivity(this.userId, this.userEmail).catch(() => {
        // Non-critical — silently ignore heartbeat failures
      });
    }
        
    // Removed inactivity timer logic
  }

  // Removed inactivity tracking method

  // Track general events with timeout protection
  async trackEvent(eventType, eventData = {}) {
    if (!this.isTracking) return;

    try {
      const appId = firestorePaths.getArtifactProjectId();
            
      let analyticsRef;
            
      if (!this.userTeam) {
        console.warn('Analytics: No team context — skipping event tracking');
        return;
      }
      analyticsRef = collection(this.db, firestorePaths.getUserActivityEventsPath(appId, this.userTeam));
            
      const eventDoc = {
        userId: this.userId,
        userEmail: this.userEmail,
        userTeam: this.userTeam,
        userTeamName: this.userTeamName,
        sessionId: this.currentSessionId,
        eventType,
        timestamp: Timestamp.now(),
        ...eventData
      };

      // Add timeout protection for analytics
      const analyticsPromise = addDoc(analyticsRef, eventDoc);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Analytics timeout after 5 seconds')), 5000)
      );

      await Promise.race([analyticsPromise, timeoutPromise]);
    } catch (error) {
      console.warn('Analytics: Error tracking event (non-critical):', error);
      // Don't re-throw - analytics failures shouldn't break the main flow
    }
  }

  // Setup activity tracking listeners
  setupActivityTracking() {
    // Don't set up listeners if tracking is disabled
    if (!this.isTracking) {
      console.log('Analytics: Skipping activity tracking setup - tracking disabled');
      return;
    }

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
    // Store bound function for cleanup
    this.boundTrackActivity = () => this.trackActivity();
        
    activityEvents.forEach(event => {
      document.addEventListener(event, this.boundTrackActivity, true);
    });

    console.log('Analytics: Activity tracking listeners attached');
    // Track initial activity
    this.trackActivity();
  }

  // Get team names for filtering (for admins only - team leads get their own team)
  async getTeamNames() {
    try {
      const appId = firestorePaths.getArtifactProjectId();
            
      // For admin users, get all teams
      if (this.isAdmin) {
        console.log('Analytics: Admin user, fetching all teams');
        const teamsRef = collection(this.db, firestorePaths.getTeamsRoot(appId));
        const snapshot = await getDocs(teamsRef);
                
        const teamNames = [];
        snapshot.docs.forEach(doc => {
          const teamData = doc.data();
          if (teamData.name) {
            teamNames.push({
              id: doc.id,
              name: teamData.name
            });
          }
        });
                
        console.log(`Analytics: Found ${teamNames.length} teams for admin`);
        return teamNames;
      }
            
      // For non-admin users, just return their own team
      if (this.userTeam && this.userTeamName) {
        console.log('Analytics: Non-admin user, returning own team only:', this.userTeamName);
        return [{
          id: this.userTeam,
          name: this.userTeamName
        }];
      }
            
      // No team available
      return [];
    } catch (error) {
      console.error('Analytics: Error getting team names:', error);
      // Fallback: return user's own team if available
      if (this.userTeam && this.userTeamName) {
        return [{
          id: this.userTeam,
          name: this.userTeamName
        }];
      }
      return [];
    }
  }

  // Helper method to get team ID from team name
  async getTeamIdFromName(teamName) {
    if (!teamName) return null;
        
    try {
      const teams = await this.getTeamNames();
      const team = teams.find(t => t.name === teamName);
      return team ? team.id : null;
    } catch (error) {
      console.error('Analytics: Error getting team ID from name:', error);
      return null;
    }
  }

  // Helper method to determine if an error is retriable
  isRetriableError(error) {
    const message = error.message.toLowerCase();
    return message.includes('timeout') || 
               message.includes('network') || 
               message.includes('unavailable') ||
               message.includes('deadline') ||
               message.includes('503') ||
               message.includes('500') ||
               message.includes('quota') ||
               message.includes('resource-exhausted');
  }

  // Static flag to track if aggregation queries have failed (to avoid repeated 400 errors)
  static aggregationIndexMissing = false;
  
  // Get accurate event counts using server-side aggregation (doesn't fetch documents)
  async getAccurateEventCounts(teamId, startDate = null, endDate = null) {
    // Skip aggregation if we know indexes are missing (persisted across page loads)
    if (AnalyticsService.aggregationIndexMissing ||
        localStorage.getItem('analytics_aggregation_index_missing') === 'true') {
      return null;
    }
    
    try {
      const appId = firestorePaths.getArtifactProjectId();
      const eventsPath = firestorePaths.getCallEventsPath(appId, teamId);
      const eventsRef = collection(this.db, eventsPath);

      // For date-filtered queries, compound indexes are required
      // If index doesn't exist, the query will fail - we catch and return null
      // which causes fallback to client-side processed metrics
      const hasDateFilter = startDate || endDate;
      
      if (hasDateFilter) {
        // Try compound query (requires [eventType, timestamp] composite index)
        const buildQuery = (eventType) => {
          const constraints = [where('eventType', '==', eventType)];
          if (startDate) constraints.push(where('timestamp', '>=', Timestamp.fromDate(startDate)));
          if (endDate) constraints.push(where('timestamp', '<=', Timestamp.fromDate(endDate)));
          return query(eventsRef, ...constraints);
        };

        // Probe with a single query first — avoids 4 parallel 400 errors if index is missing
        const probeCount = await getCountFromServer(buildQuery('call_start'));
        const [resetCount, completeCount, leadCount] = await Promise.all([
          getCountFromServer(buildQuery('call_reset')),
          getCountFromServer(buildQuery('call_complete')),
          getCountFromServer(buildQuery('lead_generated'))
        ]);
        const startCount = probeCount;

        const result = {
          callsStarted: startCount.data().count,
          callResets: resetCount.data().count,
          callCompletes: completeCount.data().count,
          leadsGenerated: leadCount.data().count,
          totalCalls: resetCount.data().count + completeCount.data().count,
          completedCalls: completeCount.data().count,
          isFiltered: true
        };
        
        console.log('Analytics: Accurate counts (date-filtered):', result);
        return result;
      } else {
        // No date filter - simple queries that don't need compound indexes
        const [startCount, resetCount, completeCount, leadCount] = await Promise.all([
          getCountFromServer(query(eventsRef, where('eventType', '==', 'call_start'))),
          getCountFromServer(query(eventsRef, where('eventType', '==', 'call_reset'))),
          getCountFromServer(query(eventsRef, where('eventType', '==', 'call_complete'))),
          getCountFromServer(query(eventsRef, where('eventType', '==', 'lead_generated')))
        ]);

        const result = {
          callsStarted: startCount.data().count,
          callResets: resetCount.data().count,
          callCompletes: completeCount.data().count,
          leadsGenerated: leadCount.data().count,
          totalCalls: resetCount.data().count + completeCount.data().count,
          completedCalls: completeCount.data().count,
          isFiltered: false
        };
        
        console.log('Analytics: Accurate counts (all-time):', result);
        return result;
      }
    } catch (error) {
      // If compound index is missing, error will be FAILED_PRECONDITION
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        // Persist flag so future page loads skip these queries entirely
        AnalyticsService.aggregationIndexMissing = true;
        try { localStorage.setItem('analytics_aggregation_index_missing', 'true'); } catch (_) { /* localStorage may be unavailable */ }
        console.warn('Analytics: Aggregation indexes missing — using client-side counts (this is normal on test environment)');
      } else {
        console.error('Analytics: Error getting accurate counts:', error);
      }
      return null;
    }
  }

  // Get analytics data for dashboard with retry logic
  async getCallAnalytics(teamNameFilter = null, startDate = null, endDate = null, retryCount = 0, overrideTeamId = null) {
    const maxRetries = 2; // Allow up to 2 retries (3 total attempts)
        
    try {
      const appId = firestorePaths.getArtifactProjectId();
            
      // Determine which team to query
      let teamIdForQuery;

      // Prefer a direct team ID override (avoids fragile name-lookup for admin viewing another team)
      if (overrideTeamId) {
        teamIdForQuery = overrideTeamId;
        console.log(`Analytics: Using overrideTeamId for query (ID: ${teamIdForQuery})`);
      } else if (teamNameFilter) {
        // Check if the team name filter matches the user's own team
        if (teamNameFilter === this.userTeamName && this.userTeam) {
          // User is filtering for their own team - use direct team ID
          teamIdForQuery = this.userTeam;
          console.log(`Analytics: Using user's own team context for "${teamNameFilter}" (ID: ${teamIdForQuery})`);
        } else {
          // Admin selected a different team - get team ID from team name
          teamIdForQuery = await this.getTeamIdFromName(teamNameFilter);
          console.log(`Analytics: Admin filtering for team "${teamNameFilter}" (ID: ${teamIdForQuery})`);
        }
      } else {
        // No team filter provided - use user's own team context for data isolation
        // SECURITY: Never query all teams - always scope to a specific team
        teamIdForQuery = this.userTeam;
        console.log(`Analytics: No filter provided - using user's team context (ID: ${teamIdForQuery})`);
      }
            
      let events = [];
            
      // SECURITY: Never query all teams - always require a specific team context
      if (!teamIdForQuery) {
        console.error('Analytics: SECURITY - No team ID available, cannot query data');
        return this.getEmptyAnalytics();
      }
      
      {
        // Single team query (existing logic)
        let collectionPath;
                
        collectionPath = firestorePaths.getCallEventsPath(appId, teamIdForQuery);
                
        let eventsQuery = collection(this.db, collectionPath);
        const constraints = [];

        // Server-side date filtering — indexes [eventType+timestamp] are now deployed
        if (startDate) {
          constraints.push(where('timestamp', '>=', Timestamp.fromDate(startDate)));
        }
        if (endDate) {
          constraints.push(where('timestamp', '<=', Timestamp.fromDate(endDate)));
        }

        constraints.push(orderBy('timestamp', 'desc'));
        // Cap events per query; accurate totals come from getCountFromServer aggregation
        constraints.push(limit(3000));

        if (constraints.length > 0) {
          eventsQuery = query(eventsQuery, ...constraints);
        }

        // Add timeout protection for dashboard queries
        const queryPromise = getDocs(eventsQuery);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Analytics query timeout after 10 seconds')), 10000)
        );
                
        const snapshot = await Promise.race([queryPromise, timeoutPromise]);
        events = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate()
        }));
      }

      console.log('Analytics: Raw call events found:', events.length);
      if (events.length > 0) {
        console.log('Analytics: Sample event:', events[0]);
        console.log('Analytics: Event types found:', [...new Set(events.map(e => e.eventType))]);
        const callStarts = events.filter(e => e.eventType === 'call_start');
        console.log('Analytics: call_start events in raw data:', callStarts.length);
      }

      // Reverse events to process in chronological order
      const chronologicalEvents = events.reverse();

      // Apply client-side filtering as a safety net (server-side filtering handles the heavy lifting)
      let filteredEvents = chronologicalEvents;
      
      console.log('Analytics: FILTERING - startDate:', startDate, 'endDate:', endDate);
            
      if (teamNameFilter) {
        filteredEvents = filteredEvents.filter(event => event.userTeamName === teamNameFilter);
        console.log('Analytics: Client-side team filter applied, events after filter:', filteredEvents.length);
      }
            
      if (startDate) {
        const beforeFilter = filteredEvents.length;
        // Debug: show sample timestamps before filtering
        if (filteredEvents.length > 0) {
          const sample = filteredEvents.slice(0, 3).map(e => ({ ts: e.timestamp, type: e.eventType }));
          console.log('Analytics: Sample event timestamps before date filter:', sample);
        }
        filteredEvents = filteredEvents.filter(event => {
          const eventTs = event.timestamp;
          const passes = eventTs && eventTs >= startDate;
          return passes;
        });
        console.log(`Analytics: Date filter (>= ${startDate.toISOString()}) applied: ${beforeFilter} -> ${filteredEvents.length} events`);
      } else {
        console.log('Analytics: NO DATE FILTER - startDate is null/undefined');
      }
            
      if (endDate) {
        const beforeEnd = filteredEvents.length;
        filteredEvents = filteredEvents.filter(event => event.timestamp <= endDate);
        console.log(`Analytics: End date filter applied: ${beforeEnd} -> ${filteredEvents.length} events`);
      }
      
      // Log filtered data
      console.log('Analytics: Final filtered events count:', filteredEvents.length);
      if (filteredEvents.length > 0) {
        const filteredCallStarts = filteredEvents.filter(e => e.eventType === 'call_start');
        console.log('Analytics: call_start events after filtering:', filteredCallStarts.length);
        const uniqueUsers = [...new Set(filteredEvents.map(e => e.userEmail))];
        console.log('Analytics: Unique users in filtered events:', uniqueUsers);
      } else {
        console.log('Analytics: No events after filtering - returning zeros');
      }

      const processedMetrics = this.processCallAnalytics(filteredEvents);
      // Get accurate counts using server-side aggregation (for single team queries)
      // This ensures dashboard totals are accurate even if not all events were fetched
      if (teamIdForQuery) {
        try {
          // Pass date filters to get accurate counts for the selected time period
          const accurateCounts = await this.getAccurateEventCounts(teamIdForQuery, startDate, endDate);
          if (accurateCounts) {
            console.log('Analytics: Using accurate server-side counts:', accurateCounts);
            // Override totals with accurate counts, keep user stats from processed events
            processedMetrics.totalCallsStarted = accurateCounts.callsStarted;
            processedMetrics.totalCalls = accurateCounts.totalCalls;
            processedMetrics.completedCalls = accurateCounts.completedCalls;
            processedMetrics.resetCalls = accurateCounts.callResets;
            // Recalculate conversion rate with accurate totals
            processedMetrics.conversionRate = accurateCounts.totalCalls > 0 
              ? ((accurateCounts.completedCalls / accurateCounts.totalCalls) * 100).toFixed(2) 
              : '0.00';
            processedMetrics.accurateCounts = true;
          }
        } catch (countError) {
          console.warn('Analytics: Could not get accurate counts:', countError);
        }
      }

      // Log userStats for debugging
      const userStatsEntries = Object.entries(processedMetrics.userStats);
      console.log('Analytics: userStats entries:', userStatsEntries.length);
      userStatsEntries.forEach(([userId, stats]) => {
        console.log(`Analytics: User ${stats.userEmail}: ${stats.callsStarted} starts, ${stats.totalCalls} finished, ${stats.leads} leads`);
      });
      
      console.log('Analytics: Final processed metrics:', {
        totalCallsStarted: processedMetrics.totalCallsStarted,
        totalCalls: processedMetrics.totalCalls,
        completedCalls: processedMetrics.completedCalls,
        resetCalls: processedMetrics.resetCalls,
        topResetPoints: processedMetrics.topResetPoints?.slice(0, 3),
        accurateCounts: processedMetrics.accurateCounts || false
      });
      return processedMetrics;
    } catch (error) {
      const attempt = retryCount + 1;
      console.error(`Analytics: Error getting call analytics (attempt ${attempt}):`, error);
            
      // Check if error is retriable and we haven't exhausted retries
      if (retryCount < maxRetries && this.isRetriableError(error)) {
        console.log(`Analytics: Retrying call analytics query in 2 seconds... (${attempt}/${maxRetries + 1})`);
                
        // Wait 2 seconds before retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.getCallAnalytics(teamNameFilter, startDate, endDate, retryCount + 1);
      }
            
      console.error('Analytics: Call analytics query failed after all retries');
      return this.getEmptyAnalytics();
    }
  }

  // Get user activity analytics
  async getUserActivityAnalytics(teamNameFilter = null, startDate = null, endDate = null, overrideTeamId = null) {
    try {
      const appId = firestorePaths.getArtifactProjectId();
            
      // Determine which team to query
      let teamIdForQuery;

      if (overrideTeamId) {
        teamIdForQuery = overrideTeamId;
        console.log(`Analytics: Using overrideTeamId for activity query (ID: ${teamIdForQuery})`);
      } else if (teamNameFilter) {
        // Admin selected a specific team - get team ID from team name
        teamIdForQuery = await this.getTeamIdFromName(teamNameFilter);
        console.log(`Analytics: Admin filtering activity for team "${teamNameFilter}" (ID: ${teamIdForQuery})`);
      } else {
        // Use current user's team context
        teamIdForQuery = this.userTeam;
        console.log(`Analytics: Using user's team context for activity (ID: ${teamIdForQuery})`);
      }
            
      let activitySnapshot, callSnapshot;
            
      if (teamIdForQuery) {
        console.log('Analytics: getUserActivityAnalytics for containerized team:', teamIdForQuery);
                
        // Get both activity events and call events from within the team's container
        // Add timeout protection for activity analytics queries
        const activityQueryPromise = getDocs(collection(this.db, firestorePaths.getUserActivityEventsPath(appId, teamIdForQuery)));
        const callQueryPromise = getDocs(collection(this.db, firestorePaths.getCallEventsPath(appId, teamIdForQuery)));
                
        const activityTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Activity query timeout after 10 seconds')), 10000)
        );
        const callTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Call events query timeout after 10 seconds')), 10000)
        );
                
        [activitySnapshot, callSnapshot] = await Promise.all([
          Promise.race([activityQueryPromise, activityTimeoutPromise]),
          Promise.race([callQueryPromise, callTimeoutPromise])
        ]);
      } else {
        console.warn('Analytics: No team context — returning empty activity data');
        return {
          totalEvents: 0, uniqueUsers: 0, eventsByType: {},
          recentEvents: [], userSummaries: [], dailyActivity: []
        };
      }
            
      // Combine events from both collections
      const activityEvents = activitySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
            
      const callEvents = callSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
            
      // Combine all events
      let allEvents = [...activityEvents, ...callEvents];
            
      // CRITICAL: Apply team filtering to prevent cross-team data leakage
      if (teamNameFilter) {
        console.log('Analytics: Applying team filter:', teamNameFilter);
        const beforeFilterCount = allEvents.length;
        allEvents = allEvents.filter(event => {
          return event.userTeamName === teamNameFilter;
        });
        console.log('Analytics: Filtered events from', beforeFilterCount, 'to', allEvents.length, 'for team:', teamNameFilter);
      }
            
      // Sort by timestamp
      allEvents.sort((a, b) => a.timestamp - b.timestamp);
            
      console.log('Analytics: Found', activityEvents.length, 'activity events and', callEvents.length, 'call events');
      console.log('Analytics: After team filtering, total events:', allEvents.length);
      if (allEvents.length > 0) {
        console.log('Analytics: Sample filtered events:', allEvents.slice(0, 5).map(e => ({
          eventType: e.eventType,
          sessionId: e.sessionId,
          userTeamName: e.userTeamName,
          timestamp: e.timestamp
        })));
      }

      return this.processActivityAnalytics(allEvents, teamNameFilter);
    } catch (error) {
      console.error('Analytics: Error getting activity analytics:', error);
      return this.getEmptyActivityAnalytics();
    }
  }

  // Get leads data for dashboard with retry logic
  async getLeadsData(teamNameFilter = null, startDate = null, endDate = null, retryCount = 0, overrideTeamId = null) {
    const maxRetries = 2; // Allow up to 2 retries (3 total attempts)
        
    try {
      const appId = firestorePaths.getArtifactProjectId();
            
      // Determine which team to query
      let teamIdForQuery;

      // overrideTeamId bypasses stale cached this.userTeam (e.g. when claims propagate after login)
      if (overrideTeamId) {
        teamIdForQuery = overrideTeamId;
        console.log(`Analytics: Using overrideTeamId for leads (ID: ${teamIdForQuery})`);
      } else if (teamNameFilter) {
        // Check if the team name filter matches the user's own team
        if (teamNameFilter === this.userTeamName && this.userTeam) {
          // User is filtering for their own team - use direct team ID
          teamIdForQuery = this.userTeam;
          console.log(`Analytics: Using user's own team context for leads "${teamNameFilter}" (ID: ${teamIdForQuery})`);
        } else {
          // Admin selected a different team - get team ID from team name
          teamIdForQuery = await this.getTeamIdFromName(teamNameFilter);
          console.log(`Analytics: Admin filtering leads for team "${teamNameFilter}" (ID: ${teamIdForQuery})`);
        }
      } else {
        // Use current user's team context
        teamIdForQuery = this.userTeam;
        console.log(`Analytics: Using user's team context for leads (ID: ${teamIdForQuery})`);
      }
            
      let allLeads = [];

      if (teamIdForQuery) {
        console.log(`Analytics: Fetching leads for containerized team: ${teamIdForQuery}`);
                
        // This query is now more complex as leads are nested under users within a team.
        // We need to get all users for the team, then get all leads for each user.
        const usersPath = firestorePaths.getUsersPath(appId, teamIdForQuery);
                
        // Add timeout protection for users query
        const usersQueryPromise = getDocs(collection(this.db, usersPath));
        const usersTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Users query timeout after 10 seconds')), 10000)
        );
        const usersSnapshot = await Promise.race([usersQueryPromise, usersTimeoutPromise]);

        for (const userDoc of usersSnapshot.docs) {
          const leadsPath = firestorePaths.getLeadsPath(appId, teamIdForQuery, userDoc.id);
                    
          // Add timeout protection for leads query
          const leadsQueryPromise = getDocs(collection(this.db, leadsPath));
          const leadsTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Leads query timeout after 10 seconds')), 10000)
          );
          const leadsSnapshot = await Promise.race([leadsQueryPromise, leadsTimeoutPromise]);
          // Derive email from the user doc data or the doc ID path
          const userDocData = userDoc.data() || {};
          const ownerEmail = userDocData.email || userDocData.userEmail || '';
          const userLeads = leadsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate(),
              // Ensure userEmail is always populated (backfill from parent user doc)
              userEmail: data.userEmail || ownerEmail,
              _ownerDocId: userDoc.id,
            };
          });
          allLeads = allLeads.concat(userLeads);
        }
      } else {
        console.warn('Analytics: No team context — returning empty leads data');
      }

      // Client-side filtering for date range (less efficient but necessary for this nested structure without composite indexes)
      if (startDate) {
        allLeads = allLeads.filter(lead => lead.timestamp >= startDate);
      }
      if (endDate) {
        allLeads = allLeads.filter(lead => lead.timestamp <= endDate);
      }

      // Sort by timestamp descending
      allLeads.sort((a, b) => b.timestamp - a.timestamp);
            
      console.log('Analytics: Total leads found for team:', allLeads.length);
      return allLeads;

    } catch (error) {
      const attempt = retryCount + 1;
      console.error(`Analytics: Error getting leads data (attempt ${attempt}):`, error);
            
      // Check if error is retriable and we haven't exhausted retries
      if (retryCount < maxRetries && this.isRetriableError(error)) {
        console.log(`Analytics: Retrying leads data query in 2 seconds... (${attempt}/${maxRetries + 1})`);
                
        // Wait 2 seconds before retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.getLeadsData(teamNameFilter, startDate, endDate, retryCount + 1);
      }
            
      console.error('Analytics: Leads data query failed after all retries');
      return [];
    }
  }

  // Process call analytics to extract metrics
  processCallAnalytics(events) {
    const metrics = {
      totalCallsStarted: 0,
      totalCalls: 0,
      completedCalls: 0,
      scriptPerformance: {},  // populated inline — avoids a second Firestore read
      resetCalls: 0,
      conversionRate: 0,
      avgCallDuration: 0,
      resetPoints: {},
      topResetPoints: [], // Will be populated with sorted reset points
      templatePerformance: {},
      hourlyDistribution: new Array(24).fill(0),
      hourlyLeads: new Array(24).fill(0),
      hourlyOutcomes: Array.from({ length: 24 }, () => ({ leadCaptured: 0, completed: 0, notConverted: 0, abandoned: 0 })),
      dailyStats: {},
      userStats: {}
    };

    const callSessions = {};

    // First pass: build session objects from events in chronological order
    events.forEach(event => {
      const sessionId = event.sessionId;
      const userId = event.userId;
      const dateKey = event.timestamp?.toISOString().split('T')[0] || 'unknown';

      // Initialize structures
      if (!callSessions[sessionId]) {
        callSessions[sessionId] = {
          startTime: null,
          endTime: null,
          status: 'started', // Default status
          resetPoint: null,
          template: null,
          userId: userId,
          dateKey: dateKey, // Store dateKey for later calculation
          userEmail: event.userEmail,
          userTeamName: event.userTeamName,
          // Outcome tracking
          reachedEndScript: false,  // visited __end_script__ node
          capturedLead: false,       // had a lead_generated event
          lastVisitedNode: null,     // last toNode before end/reset/abandon
        };
      }

      // Trust later events if call_start has a fallback template
      if (event.template && event.template !== 'unknown-template') {
        if (callSessions[sessionId].template === 'unknown-template') {
          console.log(`Analytics: Correcting template for session ${sessionId} from ${callSessions[sessionId].template} to ${event.template}`);
          callSessions[sessionId].template = event.template;
        }
      }
            
      if (!metrics.userStats[userId]) {
        metrics.userStats[userId] = {
          userEmail: event.userEmail,
          userTeamName: event.userTeamName || 'No Team',
          callsStarted: 0,
          totalCalls: 0,
          completedCalls: 0,
          resetCalls: 0,
          leads: 0,
          totalCallDuration: 0, // in milliseconds
          avgCallDuration: 0,   // in minutes
          completionRate: 0     // percentage
        };
      }
      
      // Note: callsStarted will be recalculated based on engaged sessions later
      // (sessions with at least one node_visited event)

      if (!metrics.dailyStats[dateKey]) {
        metrics.dailyStats[dateKey] = {
          calls: 0,
          completed: 0,
          resets: 0,
          leads: 0
        };
      }

      // Update session state based on event
      switch (event.eventType) {
        case 'call_start':
          callSessions[sessionId].startTime = event.timestamp;
          // Always trust the call_start event for template (it has the most accurate template info)
          if (event.template) {
            callSessions[sessionId].template = event.template;
          }
          break;

        case 'call_complete':
          if (callSessions[sessionId]) {
            callSessions[sessionId].endTime = event.timestamp;
            callSessions[sessionId].status = 'completed';
            // Use the template from call_complete event if provided (most accurate for final attribution)
            if (event.template) {
              callSessions[sessionId].template = event.template;
            }
          }
          break;

        case 'call_reset':
          if (callSessions[sessionId]) {
            callSessions[sessionId].status = 'reset';
            callSessions[sessionId].resetPoint = event.nodeId || 'unknown';
            const resetPoint = event.nodeId || 'unknown';
            metrics.resetPoints[resetPoint] = (metrics.resetPoints[resetPoint] || 0) + 1;
          }
          break;

        case 'node_visited':
          if (callSessions[sessionId]) {
            if (event.toNode === '__end_script__') {
              callSessions[sessionId].reachedEndScript = true;
            } else if (event.toNode) {
              callSessions[sessionId].lastVisitedNode = event.toNode;
            }
          }
          break;

        case 'lead_generated':
          if (metrics.userStats[userId]) metrics.userStats[userId].leads++;
          if (metrics.dailyStats[dateKey]) metrics.dailyStats[dateKey].leads++;
          if (callSessions[sessionId]) callSessions[sessionId].capturedLead = true;
          break;
      }
    });

    // Second pass: calculate metrics based on the final state of all sessions
    let totalDuration = 0;
    let completedCallsCount = 0;

    console.log('Analytics: Final state of all processed sessions:', callSessions);

    Object.values(callSessions).forEach(session => {
      // A call is only counted towards the total if it has finished
      if (session.status === 'completed' || session.status === 'reset') {
        metrics.totalCalls++;
                
        const userId = session.userId;
        const dateKey = session.dateKey;

        // Hourly distribution: bucket by the hour the call started
        if (session.startTime) {
          const hour = new Date(session.startTime).getHours();
          metrics.hourlyDistribution[hour]++;
          if (session.capturedLead) {
            metrics.hourlyLeads[hour]++;
          }
        }

        if (metrics.userStats[userId]) metrics.userStats[userId].totalCalls++;
        if (metrics.dailyStats[dateKey]) metrics.dailyStats[dateKey].calls++;

        if (session.status === 'completed') {
          metrics.completedCalls++;
          if (metrics.userStats[userId]) metrics.userStats[userId].completedCalls++;
          if (metrics.dailyStats[dateKey]) metrics.dailyStats[dateKey].completed++;
        }

        if (session.status === 'reset') {
          metrics.resetCalls++;
          if (metrics.userStats[userId]) metrics.userStats[userId].resetCalls++;
          if (metrics.dailyStats[dateKey]) metrics.dailyStats[dateKey].resets++;
        }

        // Template Performance is only calculated for finished calls
        if (session.template) {
          if (!metrics.templatePerformance[session.template]) {
            metrics.templatePerformance[session.template] = {
              totalCalls: 0,
              completedCalls: 0,
              conversionRate: 0
            };
          }
          metrics.templatePerformance[session.template].totalCalls++;
          if (session.status === 'completed') {
            metrics.templatePerformance[session.template].completedCalls++;
          }
        }
      }

      // Calculate call duration for completed/reset calls (any call with engagement)
      if ((session.status === 'completed' || session.status === 'reset') && session.startTime && session.endTime) {
        const duration = session.endTime - session.startTime;
        totalDuration += duration;
        completedCallsCount++;
        
        // Track duration per user
        const userId = session.userId;
        if (metrics.userStats[userId]) {
          metrics.userStats[userId].totalCallDuration += duration;
        }
      }
    });
    
    // Calculate per-user averages and completion rates
    Object.values(metrics.userStats).forEach(stats => {
      // Calculate completion rate
      const finishedCalls = stats.completedCalls + stats.resetCalls;
      stats.completionRate = finishedCalls > 0 
        ? Math.round((stats.completedCalls / finishedCalls) * 100) 
        : 0;
      
      // Calculate average call duration (in minutes)
      if (finishedCalls > 0 && stats.totalCallDuration > 0) {
        stats.avgCallDuration = Math.round(stats.totalCallDuration / finishedCalls / 1000 / 60 * 10) / 10; // Round to 1 decimal
      }
    });

    // Final calculations
    
    // Count engaged calls (sessions where user clicked at least one response)
    // A session is "engaged" if it has node_visited events (user interacted with script)
    // This excludes "false starts" where user just viewed the page
    let engagedCalls = 0;
    const sessionNodeCounts = {};
    const sessionToUser = {};
    
    // Count node_visited events per session and track which user owns each session
    events.forEach(event => {
      if (event.sessionId && event.userId) {
        sessionToUser[event.sessionId] = event.userId;
      }
      if (event.eventType === 'node_visited' && event.sessionId) {
        sessionNodeCounts[event.sessionId] = (sessionNodeCounts[event.sessionId] || 0) + 1;
      }
    });
    
    // Reset callsStarted for all users (we'll recalculate based on engaged sessions)
    Object.values(metrics.userStats).forEach(stats => {
      stats.callsStarted = 0;
    });
    
    // Count sessions with at least one node visit as "engaged", and compute outcomes
    const outcomes = { completedWithLead: 0, completedNoLead: 0, droppedWithLead: 0, droppedNoLead: 0, abandoned: 0 };
    const dropOffNodeCounts = {};

    Object.keys(callSessions).forEach(sessionId => {
      if (sessionNodeCounts[sessionId] > 0) {
        engagedCalls++;
        const userId = sessionToUser[sessionId];
        if (userId && metrics.userStats[userId]) {
          metrics.userStats[userId].callsStarted++;
        }

        // Classify this session's outcome
        const s = callSessions[sessionId];
        const resolved = s.status === 'completed' || s.status === 'reset';
        let outcomeKey;
        if (s.reachedEndScript && s.capturedLead)       { outcomes.completedWithLead++; outcomeKey = 'leadCaptured'; }
        else if (s.reachedEndScript && !s.capturedLead) { outcomes.completedNoLead++;   outcomeKey = 'completed'; }
        else if (resolved && s.capturedLead)            { outcomes.droppedWithLead++;    outcomeKey = 'leadCaptured'; }
        else if (resolved)                              { outcomes.droppedNoLead++;      outcomeKey = 'notConverted'; }
        else                                            { outcomes.abandoned++;          outcomeKey = 'abandoned'; }

        // Hourly outcome breakdown
        if (s.startTime) {
          const hour = new Date(s.startTime).getHours();
          if (metrics.hourlyOutcomes[hour]) {
            metrics.hourlyOutcomes[hour][outcomeKey]++;
          }
        }

        // Track drop-off node for non-end-script sessions
        if (!s.reachedEndScript && s.lastVisitedNode) {
          dropOffNodeCounts[s.lastVisitedNode] = (dropOffNodeCounts[s.lastVisitedNode] || 0) + 1;
        }

        // Accumulate per-template script performance (reuses engaged session data —
        // no second Firestore read needed)
        const tmpl = s.template;
        if (tmpl && tmpl !== 'unknown-template') {
          if (!metrics.scriptPerformance[tmpl]) {
            metrics.scriptPerformance[tmpl] = {
              calls: 0, leads: 0, rate: 0,
              endScriptCount: 0, completionRate: 0,
              _resetPoints: {}, _dropOffNodes: {},
              topResetPoints: [], topDropOffNodes: [],
            };
          }
          const sp = metrics.scriptPerformance[tmpl];
          sp.calls++;
          if (s.capturedLead) sp.leads++;
          if (s.reachedEndScript) sp.endScriptCount++;
          if (s.status === 'reset' && s.resetPoint) {
            sp._resetPoints[s.resetPoint] = (sp._resetPoints[s.resetPoint] || 0) + 1;
          }
          if (!s.reachedEndScript && s.lastVisitedNode) {
            sp._dropOffNodes[s.lastVisitedNode] = (sp._dropOffNodes[s.lastVisitedNode] || 0) + 1;
          }
        }
      }
    });

    // Finalize per-template script performance rates and sorted arrays
    Object.values(metrics.scriptPerformance).forEach(sp => {
      sp.rate = sp.calls > 0 ? parseFloat((sp.leads / sp.calls * 100).toFixed(1)) : 0;
      sp.completionRate = sp.calls > 0 ? parseFloat((sp.endScriptCount / sp.calls * 100).toFixed(1)) : 0;
      sp.topResetPoints = Object.entries(sp._resetPoints)
        .map(([nodeId, count]) => ({ nodeId, count }))
        .sort((a, b) => b.count - a.count).slice(0, 5);
      sp.topDropOffNodes = Object.entries(sp._dropOffNodes)
        .map(([nodeId, count]) => ({ nodeId, count }))
        .sort((a, b) => b.count - a.count).slice(0, 5);
      delete sp._resetPoints;
      delete sp._dropOffNodes;
    });

    metrics.sessionOutcomes = outcomes;
    metrics.topDropOffNodes = Object.entries(dropOffNodeCounts)
      .map(([nodeId, count]) => ({ nodeId, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Total calls started = engaged calls only (not just page views)
    metrics.totalCallsStarted = engagedCalls;
    console.log(`Analytics: Engaged calls: ${engagedCalls} (sessions with node_visited events)`);
    console.log('Analytics: Session outcomes:', outcomes);
    
    metrics.conversionRate = metrics.totalCalls > 0 ? 
      (metrics.completedCalls / metrics.totalCalls * 100).toFixed(2) : '0.00';

    Object.keys(metrics.templatePerformance).forEach(template => {
      const perf = metrics.templatePerformance[template];
      perf.conversionRate = perf.totalCalls > 0 ? 
        (perf.completedCalls / perf.totalCalls * 100).toFixed(2) : '0.00';
    });

    metrics.avgCallDuration = completedCallsCount > 0 ? 
      Math.round(totalDuration / completedCallsCount / 1000 / 60) : 0; // in minutes

    // Build sorted array of top reset points for Dashboard display
    metrics.topResetPoints = Object.entries(metrics.resetPoints)
      .map(([nodeId, count]) => ({ nodeId, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 drop-off points

    return metrics;
  }

  // Process activity analytics
  processActivityAnalytics(events, teamFilter = null) {
    const analytics = {
      totalSessions: new Set(),
      activeTime: 0,
      // Removed inactiveTime tracking
      avgSessionDuration: 0,
      userActivityStats: {},
      dailyActivity: {}
    };

    const sessions = {};

    events.forEach(event => {
      // Additional safety check: skip events from other teams
      if (teamFilter && event.userTeamName && event.userTeamName !== teamFilter) {
        console.log('Analytics: WARNING - Skipping event from different team:', event.userTeamName, 'expected:', teamFilter);
        return;
      }
      const sessionId = event.sessionId;
      const userId = event.userId;
      const dateKey = event.timestamp?.toISOString().split('T')[0] || 'unknown';

      analytics.totalSessions.add(sessionId);

      if (!sessions[sessionId]) {
        sessions[sessionId] = {
          startTime: null,
          activities: [],
          userId: userId
        };
      }

      if (!analytics.userActivityStats[userId]) {
        analytics.userActivityStats[userId] = {
          userEmail: event.userEmail,
          userTeamName: event.userTeamName || 'No Team',
          totalSessions: new Set(),
          activeTime: 0,
          avgSessionDuration: 0
        };
      }

      if (!analytics.dailyActivity[dateKey]) {
        analytics.dailyActivity[dateKey] = {
          activeUsers: new Set(),
          totalSessions: new Set(),
          totalActiveTime: 0
        };
      }

      analytics.userActivityStats[userId].totalSessions.add(sessionId);
      analytics.dailyActivity[dateKey].activeUsers.add(userId);
      analytics.dailyActivity[dateKey].totalSessions.add(sessionId);

      if (event.eventType === 'session_start') {
        sessions[sessionId].startTime = event.timestamp;
      }

      sessions[sessionId].activities.push(event);
    });

    // Calculate session durations and active time
    console.log('Analytics: Processing', Object.keys(sessions).length, 'sessions for duration calculation');
    console.log('Analytics: Session IDs found:', Object.keys(sessions));
    Object.values(sessions).forEach(session => {
      console.log('Analytics: Session has', session.activities.length, 'activities:', session.activities.map(a => a.eventType));
      if (session.activities.length > 1) {
        const startTime = session.activities[0].timestamp;
        const endTime = session.activities[session.activities.length - 1].timestamp;
        const duration = endTime - startTime;
                
        console.log('Analytics: Session start time:', startTime);
        console.log('Analytics: Session end time:', endTime);
        console.log('Analytics: Raw duration (ms):', duration);
        console.log('Analytics: Duration in minutes:', Math.round(duration / 1000 / 60));
                
        // Sanity check: ignore sessions longer than 24 hours (unrealistic)
        const maxSessionDuration = 24 * 60 * 60 * 1000; // 24 hours in ms
        if (duration > 0 && duration <= maxSessionDuration) {
          analytics.activeTime += duration;
                    
          if (analytics.userActivityStats[session.userId]) {
            analytics.userActivityStats[session.userId].activeTime += duration;
          }
        } else if (duration > maxSessionDuration) {
          console.log('Analytics: WARNING - Ignoring unrealistic session duration:', Math.round(duration / 1000 / 60), 'minutes');
        }
      } else {
        console.log('Analytics: Session skipped - not enough activities for duration calculation');
      }
    });

    // Convert Set to count and calculate averages
    analytics.totalSessions = analytics.totalSessions.size;
    analytics.avgSessionDuration = analytics.totalSessions > 0 ? 
      Math.round(analytics.activeTime / analytics.totalSessions / 1000 / 60) : 0; // in minutes

    // Process user stats
    Object.keys(analytics.userActivityStats).forEach(userId => {
      const userStats = analytics.userActivityStats[userId];
      userStats.totalSessions = userStats.totalSessions.size;
      userStats.avgSessionDuration = userStats.totalSessions > 0 ? 
        Math.round(userStats.activeTime / userStats.totalSessions / 1000 / 60) : 0;
    });

    // Process daily activity
    Object.keys(analytics.dailyActivity).forEach(dateKey => {
      const dayStats = analytics.dailyActivity[dateKey];
      dayStats.activeUsers = dayStats.activeUsers.size;
      dayStats.totalSessions = dayStats.totalSessions.size;
    });

    return analytics;
  }

  // Generate unique session ID
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get empty analytics structure
  getEmptyAnalytics() {
    return {
      totalCalls: 0,
      completedCalls: 0,
      resetCalls: 0,
      conversionRate: 0,
      avgCallDuration: 0,
      resetPoints: {},
      templatePerformance: {},
      hourlyDistribution: new Array(24).fill(0),
      hourlyLeads: new Array(24).fill(0),
      hourlyOutcomes: Array.from({ length: 24 }, () => ({ leadCaptured: 0, completed: 0, notConverted: 0, abandoned: 0 })),
      dailyStats: {},
      userStats: {}
    };
  }

  // Get empty activity analytics structure
  getEmptyActivityAnalytics() {
    return {
      totalSessions: 0,
      activeTime: 0,
      // Removed inactiveTime tracking
      avgSessionDuration: 0,
      userActivityStats: {},
      dailyActivity: {}
    };
  }

  // Get Call Evaluator quality scores for dashboard integration
  async getCallQualityScores(teamId = null, startDate = null, endDate = null) {
    try {
      const appId = firestorePaths.getArtifactProjectId();
      const targetTeamId = teamId || this.userTeam;
      
      if (!targetTeamId) {
        console.log('Analytics: No team ID for call quality scores');
        return this.getEmptyQualityScores();
      }

      // Call evaluations are stored at: artifacts/{appId}/teams/{teamId}/call-evaluations
      const evaluationsPath = `artifacts/${appId}/teams/${targetTeamId}/call-evaluations`;
      const evaluationsRef = collection(this.db, evaluationsPath);
      
      // Add timeout protection
      const queryPromise = getDocs(evaluationsRef);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Call quality query timeout after 10 seconds')), 10000)
      );
      
      const snapshot = await Promise.race([queryPromise, timeoutPromise]);
      
      // Filter to completed evaluations with scores
      let evaluations = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          uploadedAt: doc.data().uploadedAt?.toDate?.() || null
        }))
        .filter(e => e.status === 'completed' && e.evaluation?.overallScore);
      
      // Apply date filtering if specified
      if (startDate) {
        evaluations = evaluations.filter(e => e.uploadedAt && e.uploadedAt >= startDate);
      }
      if (endDate) {
        evaluations = evaluations.filter(e => e.uploadedAt && e.uploadedAt <= endDate);
      }
      
      if (evaluations.length === 0) {
        return this.getEmptyQualityScores();
      }
      
      // Calculate aggregate scores
      const totalScore = evaluations.reduce((sum, e) => sum + (e.evaluation.overallScore || 0), 0);
      const avgScore = Math.round(totalScore / evaluations.length);
      
      // Group by salesperson for individual scores
      const byPerson = {};
      evaluations.forEach(e => {
        const email = e.salesperson;
        if (!byPerson[email]) {
          byPerson[email] = { totalScore: 0, count: 0 };
        }
        byPerson[email].totalScore += e.evaluation.overallScore || 0;
        byPerson[email].count += 1;
      });
      
      const individualScores = Object.entries(byPerson).map(([email, data]) => ({
        email,
        avgScore: Math.round(data.totalScore / data.count),
        callCount: data.count
      })).sort((a, b) => b.avgScore - a.avgScore);
      
      // Find top performer
      const topPerformer = individualScores[0] || null;
      
      console.log(`Analytics: Call quality scores loaded - ${evaluations.length} evaluations, avg ${avgScore}`);
      
      return {
        totalEvaluations: evaluations.length,
        avgScore,
        topPerformer,
        individualScores,
        recentEvaluations: evaluations.slice(0, 5).map(e => ({
          id: e.id,
          salesperson: e.salesperson,
          score: e.evaluation.overallScore,
          date: e.uploadedAt
        }))
      };
    } catch (error) {
      console.error('Analytics: Error getting call quality scores:', error);
      return this.getEmptyQualityScores();
    }
  }

  // Empty quality scores structure
  getEmptyQualityScores() {
    return {
      totalEvaluations: 0,
      avgScore: null,
      topPerformer: null,
      individualScores: [],
      recentEvaluations: []
    };
  }

  // Get period-over-period comparison for trends
  async getTrendComparison(teamNameFilter = null, currentPeriod = 'today') {
    try {
      const now = new Date();
      let currentStart, currentEnd, previousStart, previousEnd;
      
      // Calculate current and previous period ranges
      switch (currentPeriod) {
        case 'today':
          currentStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          currentEnd = new Date(now.getTime() + 24 * 60 * 60 * 1000);
          // Previous period: yesterday
          previousStart = new Date(currentStart.getTime() - 24 * 60 * 60 * 1000);
          previousEnd = currentStart;
          break;
        case 'week':
          currentStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          currentEnd = now;
          // Previous period: week before that
          previousStart = new Date(currentStart.getTime() - 7 * 24 * 60 * 60 * 1000);
          previousEnd = currentStart;
          break;
        case 'month':
          currentStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          currentEnd = now;
          // Previous period: month before that
          previousStart = new Date(currentStart.getTime() - 30 * 24 * 60 * 60 * 1000);
          previousEnd = currentStart;
          break;
        default:
          // For "all time", we can't really calculate trends
          return { hasTrends: false, trends: {} };
      }
      
      // Fetch data for both periods in parallel
      const [currentData, previousData, currentLeads, previousLeads] = await Promise.all([
        this.getCallAnalytics(teamNameFilter, currentStart, currentEnd),
        this.getCallAnalytics(teamNameFilter, previousStart, previousEnd),
        this.getLeadsData(teamNameFilter, currentStart, currentEnd),
        this.getLeadsData(teamNameFilter, previousStart, previousEnd)
      ]);
      
      // Calculate trends for each metric
      const calculateTrend = (current, previous) => {
        if (previous === 0) {
          return current > 0 ? { value: current, direction: 'up', percentage: 100 } : { value: 0, direction: 'neutral', percentage: 0 };
        }
        const diff = current - previous;
        const percentage = Math.round((diff / previous) * 100);
        return {
          value: Math.abs(diff),
          direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral',
          percentage: Math.abs(percentage)
        };
      };
      
      // Calculate user-level trends
      const userTrends = {};
      const currentUsers = currentData?.userStats || {};
      const previousUsers = previousData?.userStats || {};
      
      Object.keys(currentUsers).forEach(userId => {
        const current = currentUsers[userId];
        const previous = previousUsers[userId] || { leads: 0, totalCalls: 0 };
        
        userTrends[current.userEmail] = {
          leads: calculateTrend(current.leads || 0, previous.leads || 0),
          calls: calculateTrend(current.totalCalls || 0, previous.totalCalls || 0)
        };
      });
      
      return {
        hasTrends: true,
        periodLabel: currentPeriod === 'today' ? 'vs yesterday' : currentPeriod === 'week' ? 'vs last week' : 'vs last month',
        trends: {
          totalCalls: calculateTrend(currentData?.totalCalls || 0, previousData?.totalCalls || 0),
          completedCalls: calculateTrend(currentData?.completedCalls || 0, previousData?.completedCalls || 0),
          totalLeads: calculateTrend(currentLeads?.length || 0, previousLeads?.length || 0),
          conversionRate: calculateTrend(
            parseFloat(currentData?.conversionRate || 0),
            parseFloat(previousData?.conversionRate || 0)
          )
        },
        userTrends
      };
    } catch (error) {
      console.error('Analytics: Error calculating trends:', error);
      return { hasTrends: false, trends: {} };
    }
  }

  // Get script performance metrics for the Scripts Dashboard
  // Returns: { templateId: { calls, leads, rate } }
  // perTemplateSince: optional { templateId: Date } — per-template cutoff dates (e.g. lastImprovedAt)
  async getScriptPerformance(startDate = null, endDate = null, perTemplateSince = {}) {
    try {
      const appId = firestorePaths.getArtifactProjectId();
      
      // Use user's team context for data isolation
      if (!this.userTeam) {
        console.warn('Analytics: No team context for script performance');
        return {};
      }
      
      const eventsPath = firestorePaths.getCallEventsPath(appId, this.userTeam);
      const eventsRef = collection(this.db, eventsPath);
      
      // Build query with date filters
      let eventsQuery;
      if (startDate && endDate) {
        eventsQuery = query(
          eventsRef,
          where('timestamp', '>=', Timestamp.fromDate(startDate)),
          where('timestamp', '<=', Timestamp.fromDate(endDate)),
          orderBy('timestamp', 'asc')
        );
      } else if (startDate) {
        eventsQuery = query(
          eventsRef,
          where('timestamp', '>=', Timestamp.fromDate(startDate)),
          orderBy('timestamp', 'asc')
        );
      } else {
        eventsQuery = query(eventsRef, orderBy('timestamp', 'asc'));
      }
      
      const snapshot = await getDocs(eventsQuery);
      const events = snapshot.docs.map(doc => ({
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      
      console.log(`Analytics: Processing ${events.length} events for script performance`);
      
      // Process events to count calls and leads per template
      const templateMetrics = {};
      const callSessions = {};
      
      events.forEach(event => {
        const sessionId = event.sessionId;
        const template = event.template;
        
        // Track session state
        if (!callSessions[sessionId]) {
          callSessions[sessionId] = {
            template: null,
            status: 'started',
            hasLead: false,
            resetPoint: null,
            latestTimestamp: null,
            reachedEndScript: false,
            lastVisitedNode: null,
          };
        }
        // Track the latest event timestamp for this session (for per-template cutoff filtering)
        if (event.timestamp && (!callSessions[sessionId].latestTimestamp || event.timestamp > callSessions[sessionId].latestTimestamp)) {
          callSessions[sessionId].latestTimestamp = event.timestamp;
        }
        
        // Update template from events (call_start and call_complete have template)
        if (template && template !== 'unknown-template') {
          callSessions[sessionId].template = template;
        }
        
        switch (event.eventType) {
          case 'call_complete':
            callSessions[sessionId].status = 'completed';
            if (template) callSessions[sessionId].template = template;
            break;
          case 'call_reset':
            callSessions[sessionId].status = 'reset';
            callSessions[sessionId].resetPoint = event.nodeId || 'unknown';
            if (template) callSessions[sessionId].template = template;
            break;
          case 'lead_generated':
            callSessions[sessionId].hasLead = true;
            if (template) callSessions[sessionId].template = template;
            break;
          case 'node_visited':
            if (event.toNode === '__end_script__') {
              callSessions[sessionId].reachedEndScript = true;
            } else if (event.toNode) {
              callSessions[sessionId].lastVisitedNode = event.toNode;
            }
            break;
        }
      });
      
      // Aggregate metrics per template
      Object.values(callSessions).forEach(session => {
        // Count finished calls (completed or reset) AND sessions that reached end-script
        // (agent navigated to __end_script__ but may not have formally reset if they closed the app)
        const isFinished = session.status === 'completed' || session.status === 'reset' || session.reachedEndScript;
        if (isFinished && session.template) {
          // Skip sessions that occurred before the per-template cutoff (lastImprovedAt)
          const cutoff = perTemplateSince[session.template];
          if (cutoff && session.latestTimestamp && session.latestTimestamp <= cutoff) return;
          if (!templateMetrics[session.template]) {
            templateMetrics[session.template] = {
              calls: 0,
              leads: 0,
              rate: 0,
              endScriptCount: 0,   // sessions that reached __end_script__
              resetPoints: {},
              dropOffNodes: {},    // last visited node before reset/abandon
            };
          }
          const tm = templateMetrics[session.template];
          tm.calls++;
          if (session.hasLead) tm.leads++;
          if (session.reachedEndScript) tm.endScriptCount++;

          // Track reset points (backwards compat)
          if (session.status === 'reset' && session.resetPoint) {
            tm.resetPoints[session.resetPoint] = (tm.resetPoints[session.resetPoint] || 0) + 1;
          }
          // Track drop-off node (last visited node for non-end-script sessions)
          if (!session.reachedEndScript && session.lastVisitedNode) {
            tm.dropOffNodes[session.lastVisitedNode] = (tm.dropOffNodes[session.lastVisitedNode] || 0) + 1;
          }
        }
      });
      
      // Calculate conversion rates, completion rates, and drop-off arrays
      Object.keys(templateMetrics).forEach(templateId => {
        const m = templateMetrics[templateId];
        m.rate = m.calls > 0
          ? parseFloat((m.leads / m.calls * 100).toFixed(1))
          : 0;
        // % of calls that reached __end_script__
        m.completionRate = m.calls > 0
          ? parseFloat((m.endScriptCount / m.calls * 100).toFixed(1))
          : 0;

        // Backwards-compat: top reset points
        m.topResetPoints = Object.entries(m.resetPoints)
          .map(([nodeId, count]) => ({ nodeId, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // New: top drop-off nodes (last node before reset/abandon, not just reset events)
        m.topDropOffNodes = Object.entries(m.dropOffNodes)
          .map(([nodeId, count]) => ({ nodeId, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
      });
      
      console.log('Analytics: Script performance metrics:', templateMetrics);
      return templateMetrics;
      
    } catch (error) {
      console.error('Analytics: Error getting script performance:', error);
      return {};
    }
  }

  /**
   * Get the count of completed call evaluations for a team.
   * Used by ImprovementsSuggester to check the minimum threshold
   * before requesting AI improvements.
   * @return {number} count of completed evaluations
   */
  async getEvaluationCountForTeam() {
    try {
      if (!this.userTeam) return 0;

      const appId = firestorePaths.getArtifactProjectId();
      const evalsPath = `artifacts/${appId}/teams/${this.userTeam}/call-evaluations`;
      const evalsRef = collection(this.db, evalsPath);
      const q = query(evalsRef, where('status', '==', 'completed'));
      const snap = await getCountFromServer(q);
      return snap.data().count;
    } catch (error) {
      console.error('Analytics: Error getting evaluation count:', error);
      return 0;
    }
  }

  /**
   * Count completed call evaluations for a specific template since a given date.
   * Used by the AI Improve screen to show the number of evaluations that will
   * actually be analyzed (i.e., those uploaded after the last improvement session).
   *
   * @param {string} templateId - The template to count evals for
   * @param {string|null} since - ISO date string cutoff (lastImprovedAt); null = count all
   * @return {number} count of matching completed evaluations
   */
  async getEvaluationCountSince(templateId, since = null) {
    try {
      if (!this.userTeam || !templateId) return 0;

      const appId = firestorePaths.getArtifactProjectId();
      const evalsPath = `artifacts/${appId}/teams/${this.userTeam}/call-evaluations`;
      const evalsRef = collection(this.db, evalsPath);

      // Only count evaluations explicitly tagged with this template.
      // Note: getTeamEvaluationCount() provides the broader team total for legacy display.
      // We intentionally do NOT fall back to all-team evaluations here because that
      // would show the same count for every script (all 169 vs. 0 tagged, etc.).
      // The Cloud Function still uses a team-wide fallback for the actual AI analysis,
      // which is fine — but the UI count should accurately reflect per-script data.
      const taggedConstraints = [
        where('assignedTemplateId', '==', templateId),
        where('status', '==', 'completed'),
      ];
      if (since) {
        taggedConstraints.push(where('uploadedAt', '>', Timestamp.fromDate(new Date(since))));
      }
      const taggedSnap = await getCountFromServer(query(evalsRef, ...taggedConstraints));
      return taggedSnap.data().count;
    } catch (error) {
      console.error('Analytics: Error getting scoped evaluation count:', error);
      return 0;
    }
  }

  /**
   * Count all completed evaluations for the team (not filtered by template).
   * Used for legacy display when evaluations lack assignedTemplateId.
   * @param {string|null} since - optional ISO date string cutoff
   * @returns {number}
   */
  async getTeamEvaluationCount(since = null) {
    try {
      if (!this.userTeam) return 0;
      const appId = firestorePaths.getArtifactProjectId();
      const evalsPath = `artifacts/${appId}/teams/${this.userTeam}/call-evaluations`;
      const evalsRef = collection(this.db, evalsPath);
      const constraints = [where('status', '==', 'completed')];
      if (since) {
        constraints.push(where('uploadedAt', '>', Timestamp.fromDate(new Date(since))));
      }
      const snap = await getCountFromServer(query(evalsRef, ...constraints));
      return snap.data().count;
    } catch (error) {
      console.error('Analytics: Error getting team evaluation count:', error);
      return 0;
    }
  }

  /**
   * Get performance impact data for a specific template, split by a cutoff date.
   * Loads all events in the combined window in a single read, then splits at the cutoff.
   * Used for the AI Improve Impact Report (before vs. after improvement comparison).
   *
   * @param {string} templateId
   * @param {Date} windowStart - Start of the before-improvement window
   * @param {Date} cutoffDate - The improvement timestamp (splits before/after)
   * @param {Date} windowEnd - End of the after-improvement window (usually now)
   * @returns {{ before: metrics, after: metrics } | null}
   */
  async getTemplateImpactData(templateId, windowStart, cutoffDate, windowEnd) {
    try {
      if (!this.userTeam || !templateId) return null;

      const appId = firestorePaths.getArtifactProjectId();
      const eventsPath = firestorePaths.getCallEventsPath(appId, this.userTeam);
      const eventsRef = collection(this.db, eventsPath);

      // Single read for the full combined window
      const eventsQuery = query(
        eventsRef,
        where('timestamp', '>=', Timestamp.fromDate(windowStart)),
        where('timestamp', '<=', Timestamp.fromDate(windowEnd)),
        orderBy('timestamp', 'asc'),
      );

      const snapshot = await getDocs(eventsQuery);
      const events = snapshot.docs.map(doc => ({
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date(),
      }));

      // Build sessions from all events
      const callSessions = {};
      events.forEach(event => {
        const sid = event.sessionId;
        if (!sid) return;
        if (!callSessions[sid]) {
          callSessions[sid] = {
            template: null,
            status: 'started',
            hasLead: false,
            resetPoint: null,
            latestTimestamp: null,
          };
        }
        if (
          event.timestamp &&
          (!callSessions[sid].latestTimestamp || event.timestamp > callSessions[sid].latestTimestamp)
        ) {
          callSessions[sid].latestTimestamp = event.timestamp;
        }
        if (event.template && event.template !== 'unknown-template') {
          callSessions[sid].template = event.template;
        }
        switch (event.eventType) {
          case 'call_complete':
            callSessions[sid].status = 'completed';
            if (event.template) callSessions[sid].template = event.template;
            break;
          case 'call_reset':
            callSessions[sid].status = 'reset';
            callSessions[sid].resetPoint = event.nodeId || 'unknown';
            if (event.template) callSessions[sid].template = event.template;
            break;
          case 'lead_generated':
            callSessions[sid].hasLead = true;
            if (event.template) callSessions[sid].template = event.template;
            break;
          default:
            break;
        }
      });

      // Filter to finished sessions for this template
      const templateSessions = Object.values(callSessions).filter(
        s =>
          s.template === templateId &&
          (s.status === 'completed' || s.status === 'reset') &&
          s.latestTimestamp,
      );

      const beforeSessions = templateSessions.filter(s => s.latestTimestamp < cutoffDate);
      const afterSessions = templateSessions.filter(s => s.latestTimestamp >= cutoffDate);

      const buildMetrics = sessions => {
        const resetPoints = {};
        let calls = 0, leads = 0, dropoffs = 0;
        sessions.forEach(s => {
          calls++;
          if (s.hasLead) leads++;
          if (s.status === 'reset' && s.resetPoint) {
            resetPoints[s.resetPoint] = (resetPoints[s.resetPoint] || 0) + 1;
            dropoffs++;
          }
        });
        const rate = calls > 0 ? parseFloat((leads / calls * 100).toFixed(1)) : 0;
        const topResetPoints = Object.entries(resetPoints)
          .map(([nodeId, count]) => ({ nodeId, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        return { calls, leads, rate, dropoffs, topResetPoints };
      };

      return {
        before: buildMetrics(beforeSessions),
        after: buildMetrics(afterSessions),
      };
    } catch (error) {
      console.error('Analytics: Error getting template impact data:', error);
      return null;
    }
  }

  /**
   * Get evaluation statistics (count + average score) for a specific template
   * within an optional time window. Used in the AI Improve Impact Report.
   *
   * @param {string} templateId
   * @param {Date|null} startDate - Inclusive lower bound on uploadedAt
   * @param {Date|null} endDate - Inclusive upper bound on uploadedAt
   * @returns {{ count: number, avgScore: number|null } | null}
   */
  async getTemplateEvalStats(templateId, startDate = null, endDate = null) {
    try {
      if (!this.userTeam || !templateId) return null;

      const appId = firestorePaths.getArtifactProjectId();
      const evalsPath = `artifacts/${appId}/teams/${this.userTeam}/call-evaluations`;
      const evalsRef = collection(this.db, evalsPath);

      const constraints = [
        where('assignedTemplateId', '==', templateId),
        where('status', '==', 'completed'),
      ];
      if (startDate) constraints.push(where('uploadedAt', '>=', Timestamp.fromDate(startDate)));
      if (endDate) constraints.push(where('uploadedAt', '<=', Timestamp.fromDate(endDate)));

      const q = query(evalsRef, ...constraints);
      const snapshot = await getDocs(q);

      const scores = [];
      snapshot.forEach(doc => {
        const score = doc.data().evaluation?.overallScore;
        if (score != null) scores.push(score);
      });

      return {
        count: snapshot.size,
        avgScore:
          scores.length > 0
            ? parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1))
            : null,
      };
    } catch (error) {
      console.error('Analytics: Error getting eval stats:', error);
      return null;
    }
  }

  // Cleanup
  destroy() {
    this.isTracking = false;
        
    // Remove event listeners to prevent memory leaks
    if (this.boundTrackActivity) {
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      activityEvents.forEach(event => {
        document.removeEventListener(event, this.boundTrackActivity, true);
      });
      console.log('Analytics: Activity tracking listeners removed');
      this.boundTrackActivity = null;
    }
        
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }

    // Track session end
    if (this.currentSessionId) {
      this.trackEvent('session_end', {
        sessionId: this.currentSessionId,
        duration: new Date() - this.sessionStartTime
      });
    }
  }
}

export default AnalyticsService;
